// src/utils/ColorPicker.js

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Popover,
  Typography,
  TextField,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';

// Icons matching ConfirmDialog style
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// LOGICAL 12x4 PALETTE
const PRESET_COLORS = [
  // Row 1: Dark / Deep Shades
  '#000000',
  '#424242',
  '#720000',
  '#8B4500',
  '#8B8B00',
  '#004200',
  '#004D40',
  '#000080',
  '#21004F',
  '#4A148C',
  '#4E342E',
  '#3E2723',
  // Row 2: Standard / Vibrant
  '#616161',
  '#F44336',
  '#FF6D00',
  '#FFD600',
  '#2E7D32',
  '#00BCD4',
  '#00B0FF',
  '#6200EA',
  '#AA00FF',
  '#C2185B',
  '#795548',
  '#5D4037',
  // Row 3: Light / Bright
  '#9E9E9E',
  '#EF5350',
  '#FF9900',
  '#FFFF00',
  '#64DD17',
  '#18FFFF',
  '#2979FF',
  '#7C4DFF',
  '#D500F9',
  '#E91E63',
  '#A1887F',
  '#8D6E63',
  // Row 4: Pastel / Pale
  '#F5F5F5',
  '#FFCDD2',
  '#FFE0B2',
  '#FFF9C4',
  '#B9F6CA',
  '#E0F7FA',
  '#E3F2FD',
  '#E1BEE7',
  '#F3E5F5',
  '#FCE4EC',
  '#D7CCC8',
  '#FFFFFF',
];

const ColorPicker = ({
  value = '#000000',
  onChange,
  label = 'Select Color',
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [internalColor, setInternalColor] = useState(value);
  const originalColorRef = useRef(value);

  const open = Boolean(anchorEl);
  const id = open ? 'color-picker-popover' : undefined;

  // --- THE STATE SHIELD ---
  // Only accept outside value changes if the popover is CLOSED.
  // This physically prevents the "double-click" bug where the parent's
  // delayed state update overwrites the user's immediate single click.
  useEffect(() => {
    if (!open) {
      setInternalColor(value);
      originalColorRef.current = value;
    }
  }, [value, open]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') return;
    setAnchorEl(null);
  };

  const handleConfirm = () => {
    // Guarantee the parent is notified on OK click
    if (onChange) {
      onChange(internalColor);
    }
    setAnchorEl(null);
  };

  const handleCancel = () => {
    // Revert to the original color if they cancel
    setInternalColor(originalColorRef.current);
    setAnchorEl(null);
  };

  const handleColorSelect = (color) => {
    const hex = color.toUpperCase();
    // AMENDED: Set internal color ONLY. Do not fire onChange until OK is clicked.
    setInternalColor(hex);
  };

  const handleCustomChange = (e) => {
    const newVal = e.target.value.toUpperCase();
    // AMENDED: Set internal color ONLY. Do not fire onChange until OK is clicked.
    setInternalColor(newVal);
  };

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          onClick={handleClick}
          sx={{
            border: '1px solid #ddd',
            p: 0.5,
            width: 40,
            height: 40,
            ...sx,
          }}>
          <Box
            sx={{
              backgroundColor: internalColor,
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ zIndex: 100020 }} // AMENDED: Brought into the standard Dropdown tier
        slotProps={{
          backdrop: { sx: { backgroundColor: 'transparent' } },
          paper: { sx: { boxShadow: '0px 4px 20px rgba(0,0,0,0.15)', borderRadius: 2 } },
        }}>
        <Box sx={{ p: 2, width: 340 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 1.5,
            }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
              {label}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, 1fr)',
              gap: 0.75,
              mb: 2,
            }}>
            {PRESET_COLORS.map((color, index) => (
              <Box
                key={`${color}-${index}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleColorSelect(color);
                }}
                sx={{
                  width: '100%',
                  aspectRatio: '1/1',
                  backgroundColor: color,
                  cursor: 'pointer',
                  borderRadius: '50%',
                  border:
                    color.toUpperCase() === '#FFFFFF'
                      ? '1px solid #ddd'
                      : '1px solid rgba(0,0,0,0.1)',
                  boxShadow:
                    (internalColor || '').toUpperCase() === color.toUpperCase()
                      ? '0 0 0 2px white, 0 0 0 4px #2196F3'
                      : 'none',
                  position: 'relative',
                  zIndex:
                    (internalColor || '').toUpperCase() === color.toUpperCase() ? 2 : 1,
                  // Keep hit-boxes perfectly stable using opacity instead of scale
                  transition: 'opacity 0.2s',
                  '&:hover': { opacity: 0.7, zIndex: 10 },
                }}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1.5,
            }}>
            <TextField
              size='small'
              variant='outlined'
              value={internalColor}
              onChange={handleCustomChange}
              placeholder='#000000'
              inputProps={{ maxLength: 7 }}
              sx={{
                width: '110px',
                '& .MuiOutlinedInput-root': { borderRadius: '20px' },
              }}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleCancel}
                sx={{ color: '#d32f2f' }}
                title='Cancel & Revert'>
                <CancelIcon sx={{ fontSize: 32 }} />
              </IconButton>
              <IconButton onClick={handleConfirm} sx={{ color: '#2e7d32' }} title='Ok'>
                <CheckCircleIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ColorPicker;
